import { configWithWebToken } from "./config";
import { IFood } from "../models/IFood";
import { IRestaurantDetail } from "../models/IRestaurantDetail";
import { getMenuByRestaurantId } from "./food";
import { getRestaurantById } from "./restaurants";

export interface ISearchResult {
  id: string;
  type: string;
  name: string;
  /** "Direct": adı eşleşti. "Category": aynı kategoride olduğu için sunucu ekledi. */
  matchType?: "Direct" | "Category";
  matchedCategoryId?: string | null;
  matchedCategoryName?: string | null;
  /** Yemek satırında yemeğin kendi id'si (id alanı restoranın id'si). Eski API'de yok. */
  foodId?: string | null;
}

/** Yemek satırının adı "Yemek Adı, Restoran Adı" biçiminde gelir — yemek kısmını ayırır. */
export const foodNameFromRow = (rowName: string) => {
  const commaIdx = rowName.lastIndexOf(", ");
  return (commaIdx !== -1 ? rowName.slice(0, commaIdx) : rowName).trim();
};

export const searchQuery = (query: string) => {
  // /api/Search anonim bir uç (bkz. Documentation/SearchController.md) — giriş
  // şartı yok. configWithUserToken kullanmak, hiç token'ı olmayan misafirlerin
  // isteği ağa çıkmadan istemci tarafında iptal edilmesine (no_user_token) yol
  // açıyordu. configWithWebToken uygulama-düzeyi token'ı her zaman taşır,
  // misafir/giriş yapmış farketmeksizin çalışır.
  return configWithWebToken.get<ISearchResult[]>("Search", { params: { query } });
};

export interface IAdvancedSearchParams {
  query?: string;
  foodCategoryId?: string;
  type?: "Restaurant" | "Food";
  page?: number;
  pageSize?: number;
}

export interface IAdvancedSearchResponse {
  items: ISearchResult[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Gelişmiş arama. /api/Search/advanced de anonim bir uç.
 *
 * Kategori tek başına geçerli bir aramadır (query göndermeye gerek yok) — ana
 * sayfadaki kategori şeridi sadece foodCategoryId yollar. Metin tek başınaysa
 * sunucu en az 3 karakter ister.
 */
export const advancedSearch = (params: IAdvancedSearchParams) =>
  configWithWebToken.post<IAdvancedSearchResponse>("Search/advanced", {
    query: params.query ?? "",
    foodCategoryId: params.foodCategoryId ?? null,
    type: params.type ?? null,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });

export interface IRestaurantFoodGroup {
  restaurant: IRestaurantDetail;
  foods: IFood[];
}

/** Aksan ve büyük/küçük harf duyarsız karşılaştırma için — sunucudaki collation gibi. */
const normalize = (text: string) =>
  text
    .trim()
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ı/g, "i");

/**
 * Arama sonuçlarını restorana göre gruplanmış getirir: metinle ("tavuk"),
 * kategoriyle ya da ikisiyle.
 *
 * Kategori tek başına bir aramadır (bkz. Documentation/SearchController.md):
 * query boş gidebilir, o zaman yalnızca yemekler döner. Arama satırında resim
 * ya da fiyat yok, yemek satırının id'si de restoranın Id'sidir. Bu yüzden
 * aramadan sadece restoran Id'lerini alıp her restoranın detayını (logo) ve
 * menüsünü (resim, fiyat) ayrıca çekiyoruz; eşleşen yemekler menüden seçilir.
 * Adıyla eşleşen bir restoranda eşleşen yemek yoksa menüsünün tamamı gösterilir.
 */
export const searchGroupedByRestaurant = async (params: {
  query?: string;
  foodCategoryId?: string;
}): Promise<IRestaurantFoodGroup[]> => {
  const query = params.query?.trim() ?? "";
  const categoryId = params.foodCategoryId;

  // Her restoranın menüsü ayrıca çekildiği için ilk birkaç sayfa yeterli.
  const MAX_PAGES = 4;
  const restaurantIds: string[] = [];
  const matchedByName = new Set<string>();
  // Sunucunun döndürdüğü yemek satırları, restoran bazında (satır id'si restoranın id'si).
  // Kategori genişletmesiyle gelenlerin adı aranan metni içermez — "mantar" araması aynı
  // kategorideki "Tavuk Roll"u da getirir — bu yüzden menüyü sadece ada göre süzmek onları atıyordu.
  // Eşleştirme foodId ile; foodId dönmeyen eski API'de satır adıyla ("Yemek, Restoran").
  type FoundKeys = { ids: Set<string>; names: Set<string> };
  const newKeys = (): FoundKeys => ({ ids: new Set(), names: new Set() });
  const found = new Map<string, { direct: FoundKeys; category: FoundKeys }>();
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await advancedSearch({
      query: query || undefined,
      foodCategoryId: categoryId,
      type: query ? undefined : "Food",
      page,
      pageSize: 50,
    });
    for (const item of res.data.items) {
      if (!restaurantIds.includes(item.id)) restaurantIds.push(item.id);
      if (item.type === "Restaurant") matchedByName.add(item.id);
      if (item.type === "Food") {
        const entry = found.get(item.id) ?? { direct: newKeys(), category: newKeys() };
        const keys = item.matchType === "Category" ? entry.category : entry.direct;
        if (item.foodId) keys.ids.add(item.foodId);
        if (item.name) keys.names.add(normalize(foodNameFromRow(item.name)));
        found.set(item.id, entry);
      }
    }
    if (!res.data.hasNextPage) break;
  }

  const needle = normalize(query);
  const inKeys = (f: IFood, keys?: FoundKeys) =>
    !!keys &&
    (keys.ids.has(f.id) || keys.names.has(normalize(f.foodNameTr)) || keys.names.has(normalize(f.foodNameEn ?? "")));
  const isDirectMatch = (f: IFood, restaurantId: string) =>
    !needle ||
    normalize(f.foodNameTr).includes(needle) ||
    normalize(f.foodNameEn ?? "").includes(needle) ||
    inKeys(f, found.get(restaurantId)?.direct);
  const matchesFood = (f: IFood, restaurantId: string) =>
    f.foodIsEnabled &&
    (!categoryId || f.foodCategoryId === categoryId) &&
    (isDirectMatch(f, restaurantId) || inKeys(f, found.get(restaurantId)?.category));

  // Tek bir restoranın detayı ya da menüsü gelmezse diğerleri yine gösterilir.
  const groups = await Promise.all(
    restaurantIds.map(async (id): Promise<IRestaurantFoodGroup | null> => {
      try {
        const [restaurant, menu] = await Promise.all([getRestaurantById(id), getMenuByRestaurantId(id)]);
        // Adı eşleşenler önce, kategoriden gelenler sonra — sunucunun sırasıyla aynı
        let foods = menu.data
          .filter((f) => matchesFood(f, id))
          .sort((a, b) => Number(isDirectMatch(b, id)) - Number(isDirectMatch(a, id)));
        if (foods.length === 0 && matchedByName.has(id)) {
          foods = menu.data.filter((f) => f.foodIsEnabled);
        }
        if (foods.length === 0) return null;
        return { restaurant: { ...restaurant.data, id }, foods };
      } catch {
        return null;
      }
    })
  );

  // Açık restoranlar üstte, kapalılar altta; her grubun kendi sırası korunur.
  return groups
    .filter((g): g is IRestaurantFoodGroup => g !== null)
    .sort((a, b) => Number(isRestaurantOpen(b.restaurant)) - Number(isRestaurantOpen(a.restaurant)));
};

/** isOpenNow yoksa (eski yanıt) detaydaki restourantIsEnabled aynı anlamda döner. */
export const isRestaurantOpen = (r: IRestaurantDetail) => r.isOpenNow ?? r.restourantIsEnabled;
